import clsx from "clsx";
import React from "react";
import {
  Container,
  ContainerProps,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepButton,
  useMediaQuery,
  StepLabel,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useCurrentBrand } from "../../hooks/useCurrentWorkspace";
import { useQuery } from "@tanstack/react-query";
import {
  BrandedAppStatus,
  UserInfoDto,
} from "@growth-machine-llc/stridist-api-client";
import WorkspacesService from "../../services/WorkspacesService";

import { useNavigate } from "react-router-dom";
import { COACH_MESSAGE_ROUTE } from "../../routes/routes";
import { RequestedBrandedAppContent } from "./branded-app/RequestedBrandedAppContent";
import { ConfigurationContent } from "./branded-app/ConfigurationContent";
import { PendingReviewContent } from "./branded-app/PendingReviewContent";
import { AppInProgressContent } from "./branded-app/AppInProgressContent";
import { ReadyForPublishContent } from "./branded-app/ReadyForPublishContent";
import { PublishedAppContent } from "./branded-app/PublishedAppContent";
import { MessageCircleMore } from "lucide-react";
import { BrandedAppAdvertisingContent } from "./branded-app/BrandedAppAdvertisingContent";
import { useOptimisticUpdateMutation } from "../../hooks/useOptimisticUpdateMutation";
import { CURRENT_USER_QUERY_KEY } from "../../wrappers/current-user/CurrentUserWrapper";
import AdminService from "../../services/AdminService";
import { useCurrentUser } from "../../hooks/useCurrentUser";

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: theme.breakpoints.values.slg,
    padding: theme.spacing(3, 2),

    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(6, 4),
    },
  },
  header: {
    marginBottom: theme.spacing(2.5),
  },

  features: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    marginBottom: theme.spacing(4.5),
    marginTop: theme.spacing(3),
  },
  feature: {
    "&:not(:last-child)": {
      marginBottom: theme.spacing(3.5),
    },

    [theme.breakpoints.up("md")]: {
      "&:not(:last-child)": {
        marginBottom: theme.spacing(3.5),
      },
    },
  },
  check: {
    marginRight: theme.spacing(1.25),

    "& svg": {
      width: 30,
      height: 30,
      color: theme.palette.primary.main,
    },
  },
  text: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  links: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(1),
    alignItems: "center",
    flexDirection: "column",
  },

  titleBox: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    flexDirection: "column",
    marginBottom: theme.spacing(2),

    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      gap: theme.spacing(2),
      marginBottom: 0,
    },
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },

  statusBox: {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 2),
    flexShrink: 0,
    marginBottom: 4,
  },
  status: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
  },

  centerColumn: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    gap: theme.spacing(5),
  },

  uploadContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),

    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      gap: theme.spacing(4),
      alignItems: "flex-start",
      justifyContent: "space-between",
    },
  },

  uploadButton: {
    display: "flex",
    gap: theme.spacing(1),
    maxWidth: theme.breakpoints.values.sm,
  },

  uploadSection: {
    width: "100%",
    maxWidth: 400,
  },

  submitButton: {
    display: "flex",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(5),
    height: theme.spacing(8),
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1,
    padding: theme.spacing(1, 2),

    "&:hover": {
      backgroundColor: theme.palette.primary.light,
    },
  },

  contactUsButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,

    "&:hover": {
      backgroundColor: theme.palette.primary.light,
    },
  },

  uploadPreview: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",

    [theme.breakpoints.up("md")]: {
      width: theme.breakpoints.values.sm,
    },
  },

  sharablePreview: {
    paddingTop: theme.spacing(-2),
    maxWidth: theme.spacing(44),
  },

  sublist: {
    marginTop: theme.spacing(-3),
    marginLeft: theme.spacing(8),
    paddingLeft: theme.spacing(2),
    listStyleType: "disc",
  },

  contactUsBanner: {
    marginTop: theme.spacing(5),
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.border.tertiary,
    padding: theme.spacing(1),
    borderRadius: theme.spacing(2),
    borderColor: theme.palette.border.secondary,
    borderWidth: theme.spacing(0.2),
    borderStyle: "solid",
  },

  p: {
    margin: theme.spacing(1, 0),
  },

  a: {
    color: theme.palette.primary.main,

    "&:hover": {
      color: theme.palette.primary.light,
    },
  },
}));

const BRANDED_APP_MANAGER_USERNAME_KEY = "brandedAppManagerUsername";

export interface CoachBrandedAppInfoProps
  extends Omit<ContainerProps, "children"> {}

export function CoachBrandedAppInfo(props: CoachBrandedAppInfoProps) {
  const { className, ...other } = props;
  const s = useStyles();
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const navigate = useNavigate();

  const { brandedAppId, brandedAppStatus } = useCurrentBrand();
  const { admin: isAdmin } = useCurrentUser();

  const { data: brandedAppManagerUsername } = useQuery({
    queryKey: [BRANDED_APP_MANAGER_USERNAME_KEY],
    queryFn: WorkspacesService.getBrandedAppManagerUsername,
  });

  const { mutate: updateStatus } = useOptimisticUpdateMutation({
    mutationFn: AdminService.changeBrandedAppStatus,
    queryKey: [CURRENT_USER_QUERY_KEY],
    optimisticUpdater: {
      updateFn(oldData: UserInfoDto, variables, tempId) {
        if (!oldData?.workspace?.brandedApp) {
          return oldData;
        }

        return UserInfoDto.fromJS({
          ...oldData,
          workspace: {
            ...oldData.workspace,
            brandedApp: {
              ...oldData.workspace.brandedApp,
              status: variables.status,
            },
          },
        });
      },
    },
    disableToastAlerts: true,
  });

  const handleUpdateStatus = (status: BrandedAppStatus) => {
    updateStatus({
      status,
      appId: brandedAppId,
      skipValidation: true,
    });
  };

  const backendFoundBrandedAppManagerToMessageTo =
    !!brandedAppManagerUsername?.username;

  const brandedAppManagerMessageHandler =
    backendFoundBrandedAppManagerToMessageTo &&
    (() => {
      navigate(
        COACH_MESSAGE_ROUTE.replace(
          ":recipient",
          brandedAppManagerUsername?.username,
        ),
      );
    });

  const isInactiveStatus = (status: BrandedAppStatus) => {
    return (
      status === BrandedAppStatus.None ||
      status === BrandedAppStatus.Cancelled
    );
  };

  const renderContent = (status: BrandedAppStatus) => {
    switch (status) {
      case BrandedAppStatus.Requested:
        return <RequestedBrandedAppContent />;
      case BrandedAppStatus.Configuration:
        return (
          <ConfigurationContent
            contactSupport={brandedAppManagerMessageHandler}
          />
        );
      case BrandedAppStatus.PendingReview:
        return <PendingReviewContent />;
      case BrandedAppStatus.ReadyForAppBuild:
        return <AppInProgressContent />;
      case BrandedAppStatus.ReadyForPublish:
        return <ReadyForPublishContent />;
      case BrandedAppStatus.Published:
        return <PublishedAppContent />;
      default:
        return <BrandedAppAdvertisingContent />;
    }
  };

  const steps = {
    [BrandedAppStatus.Requested]: "Requested",
    [BrandedAppStatus.Configuration]: "Configuration",
    [BrandedAppStatus.PendingReview]: "Pending Review",
    [BrandedAppStatus.ReadyForAppBuild]: "In Development",
    [BrandedAppStatus.ReadyForPublish]: "Ready for Publish",
    [BrandedAppStatus.Published]: "Published",
  };

  const getStepIndex = (step: BrandedAppStatus) => {
    return Object.values(BrandedAppStatus).indexOf(step);
  };

  return (
    <Container className={clsx(s.root, className)} {...other}>
      {mdUp &&
        brandedAppStatus &&
        !isInactiveStatus(brandedAppStatus) && (
          <Stepper
            nonLinear
            activeStep={
              Object.values(BrandedAppStatus).indexOf(brandedAppStatus) - 1
            }
            sx={{
              marginBottom: 4,
            }}
          >
            {Object.entries(steps).map(([status, label]) => (
              <Step
                key={status}
                completed={
                  getStepIndex(brandedAppStatus) >
                  getStepIndex(status as BrandedAppStatus)
                }
              >
                {isAdmin ? (
                  <StepButton
                    onClick={() =>
                      handleUpdateStatus(status as BrandedAppStatus)
                    }
                  >
                    {label}
                  </StepButton>
                ) : (
                  <StepLabel>{label}</StepLabel>
                )}
              </Step>
            ))}
          </Stepper>
        )}
      {renderContent(brandedAppStatus)}
      {backendFoundBrandedAppManagerToMessageTo && (
        <Box className={s.contactUsBanner}>
          <Typography variant="body2" fontWeight={500} sx={{ marginLeft: 1 }}>
            {brandedAppStatus && !isInactiveStatus(brandedAppStatus) ? (
              "If you have questions or need help at any point during the process, please contact us."
            ) : (
              <p className={s.p}>
                Ready to have your own app? Contact{" "}
                <a className={s.a} href="mailto:accounts@stridist.com">
                  accounts@stridist.com
                </a>{" "}
                and we'll get you started
              </p>
            )}
          </Typography>
          {brandedAppStatus && !isInactiveStatus(brandedAppStatus) && (
            <Button
              className={s.contactUsButton}
              onClick={brandedAppManagerMessageHandler}
            >
              <MessageCircleMore />
            </Button>
          )}
        </Box>
      )}
    </Container>
  );
}
